/**
 * Reparto de las LIVE alerts de EulerStream entre agencias.
 *
 * Regla de negocio (Fran, sep-2026): las alerts (aviso al instante) son un
 * recurso limitado y se reparten POR PLAN de agencia — Titanium 100, Gold 50,
 * Silver y Rookie 0 — entre los «creadores activos» de cada agencia, con la
 * misma ventana de 7 días que usa esa tarjeta del Backstage de TikTok: quien
 * ha emitido en los últimos 7 días. Si hay más activos que cupo, entran los
 * de más diamantes en esos 7 días. El resto sigue vigilado por el sondeo en
 * bloque (bulk check, cada 2 min), que también abre sesión y espectador
 * cuando alguien sale en directo.
 *
 * Agencia sin Backstage sincronizado: activo = ha emitido en 7 días según
 * nuestro tracking, ordenado por sus diamantes de 7 días.
 *
 * Una agencia puede tener otro cupo a medida en
 * agencies/{id}.planOverrides.limits.maxAlerts.
 *
 * Módulo puro (sin D1 ni Firestore) para poder testearlo con node.
 */

export const ALERT_QUOTA_BY_PLAN = { titanium: 100, gold: 50, silver: 0, rookie: 0 };
export const ACTIVE_WINDOW_MS = 7 * 86400000;

/** Cupo de alerts de una agencia según su plan efectivo (+ override a medida). */
export function alertQuotaFor(planId, planOverrides) {
    const lim = planOverrides && typeof planOverrides === 'object' && planOverrides.limits && typeof planOverrides.limits === 'object'
        ? planOverrides.limits.maxAlerts
        : undefined;
    if (lim != null && lim !== '' && Number.isFinite(Number(lim))) return Math.max(0, Math.floor(Number(lim)));
    const base = ALERT_QUOTA_BY_PLAN[String(planId || '').toLowerCase()];
    return Number.isFinite(base) ? base : 0;
}

/**
 * Pone `active` y `score` a cada fila (agency_id, unique_id). Por agencia:
 *  · con Backstage (alguna fila trae bs7/bs_active): active = bs_active o
 *    último directo en 7 días (last_live_at, que ya junta Backstage y
 *    tracking); score = bs7 (diamantes de la semana según el Backstage; si
 *    ese creador aún no tiene el diario leído, sus d7 del tracking);
 *  · sin Backstage: active = last_live_at en los últimos 7 días, score = d7.
 * Muta las filas y devuelve { agency_id → 'backstage' | 'tracking' }.
 */
export function scoreRows(rows, nowMs = Date.now()) {
    const hasBackstage = new Set();
    for (const r of rows || []) {
        if (r && r.agency_id && ((r.bs7 != null && Number.isFinite(Number(r.bs7))) || r.bs_active != null)) hasBackstage.add(r.agency_id);
    }
    const cutoff = (Number(nowMs) || Date.now()) - ACTIVE_WINDOW_MS;
    const source = {};
    for (const r of rows || []) {
        if (!r || !r.agency_id) continue;
        if (hasBackstage.has(r.agency_id)) {
            // Activo si lo dice el Backstage O si nuestro tracking lo vio en
            // directo esta semana: el diario del Backstage va con 1-2 días de
            // retraso (hoy no se lee hasta mañana) y sin esto quien emitió
            // ayer por primera vez en semanas quedaba fuera.
            r.active = Number(r.bs_active) === 1 || (Number(r.last_live_at) || 0) >= cutoff;
            r.score = Math.max(0, Number(r.bs7 != null ? r.bs7 : r.d7) || 0);
            source[r.agency_id] = 'backstage';
        } else {
            r.active = (Number(r.last_live_at) || 0) >= cutoff;
            r.score = Math.max(0, Number(r.d7) || 0);
            source[r.agency_id] = 'tracking';
        }
    }
    return source;
}

/**
 * Orden del ranking: más puntuación primero (score; si falta, d7); a igualdad,
 * quien emitió más recientemente; a igualdad, por @ (estable).
 */
export function compareByRank(a, b) {
    const sa = a.score != null ? Number(a.score) || 0 : Number(a.d7) || 0;
    const sb = b.score != null ? Number(b.score) || 0 : Number(b.d7) || 0;
    if (sb !== sa) return sb - sa;
    const l = (Number(b.last_live_at) || 0) - (Number(a.last_live_at) || 0);
    if (l) return l;
    return String(a.unique_id).localeCompare(String(b.unique_id));
}

/**
 * Quién debe tener alert: de cada agencia, sus creadores ACTIVOS (filas con
 * `active !== false`) mejor puntuados, hasta el cupo. `quotas` = Map
 * agency_id → cupo; agencia sin cupo conocido → 0. Devuelve un Set de
 * unique_id (un creador en dos agencias entra si entra por cualquiera).
 */
export function pickAlertEligible(rows, quotas) {
    const byAgency = new Map();
    for (const r of rows || []) {
        if (!r || !r.agency_id || !r.unique_id || r.active === false) continue;
        if (!byAgency.has(r.agency_id)) byAgency.set(r.agency_id, []);
        byAgency.get(r.agency_id).push(r);
    }
    const eligible = new Set();
    for (const [agencyId, list] of byAgency) {
        const quota = Math.max(0, Number(quotas && quotas.get ? quotas.get(agencyId) : 0) || 0);
        if (!quota) continue;
        list.sort(compareByRank);
        for (const r of list.slice(0, quota)) eligible.add(r.unique_id);
    }
    return eligible;
}

/**
 * Agencias con filas activas en el tracking que ya no existen en Firestore
 * (borradas o renombradas): sus creadores no deben seguir vigilados.
 * Si la lista de agencias vino vacía no se considera fantasma a nadie: una
 * lectura fallida no puede apagar todo el tracking.
 */
export function ghostAgencyIds(activeAgencyIds, knownAgencyIds) {
    const known = new Set(knownAgencyIds || []);
    if (!known.size) return [];
    return [...new Set(activeAgencyIds || [])].filter((id) => id && !known.has(id));
}
